import React, { useState, useRef, useEffect, useMemo } from 'react';
import { X, Check } from 'lucide-react';
import './MultiSelectSearchable.css';

const MultiSelectSearchable = ({
  options = [],
  selected = [],
  onChange,
  placeholder = 'Type to search...',
  disabled = false,
  className = '',
  getOptionLabel = (option) => option.name || option.label || option,
  getOptionValue = (option) => option.id || option.value || option,
  getOptionEmail = (option) => option.email || '',
  getOptionClient = (option) => option.client || option.clientName || '',
  loading = false,
  noOptionsText = 'No contacts found',
  label,
  showClientFilter = false,
  clients = []
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedClient, setSelectedClient] = useState('');
  
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const optionsRef = useRef(null);

  // Filter options based on search query and client
  const filteredOptions = useMemo(() => {
    let filtered = options;
    
    // Filter by client if selected
    if (selectedClient && showClientFilter) {
      filtered = filtered.filter(option => {
        const clientName = getOptionClient(option);
        return clientName === selectedClient;
      });
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(option => {
        const label = getOptionLabel(option);
        const email = getOptionEmail(option);
        
        // Handle null/undefined values
        const labelStr = (label || '').toString().toLowerCase();
        const emailStr = (email || '').toString().toLowerCase();
        
        return labelStr.includes(query) || emailStr.includes(query);
      });
    }
    
    return filtered;
  }, [options, searchQuery, selectedClient, showClientFilter, getOptionLabel, getOptionEmail, getOptionClient]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex(prev => 
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex(prev => 
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
          break;
        case 'Enter':
          event.preventDefault();
          if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
            handleToggleOption(filteredOptions[highlightedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;
        default:
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, highlightedIndex, filteredOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionsRef.current) {
      const highlightedElement = optionsRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleToggleOption = (option) => {
    const optionValue = getOptionValue(option);
    const isSelected = selected.includes(optionValue);
    
    if (isSelected) {
      onChange(selected.filter(val => val !== optionValue));
    } else {
      onChange([...selected, optionValue]);
      setSearchQuery(''); // Clear search after selecting
    }
  };

  const handleRemoveOption = (optionValue, e) => {
    e.stopPropagation();
    onChange(selected.filter(val => val !== optionValue));
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    onChange([]);
    setSearchQuery('');
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
      inputRef.current?.focus();
    }
  };

  // Get selected option objects
  const selectedOptions = useMemo(() => {
    return selected.map(val => 
      options.find(opt => getOptionValue(opt) === val)
    ).filter(Boolean);
  }, [selected, options, getOptionValue]);

  return (
    <div 
      className={`multi-select-searchable ${className} ${disabled ? 'disabled' : ''}`} 
      ref={dropdownRef}
    >
      {label && <label className="multi-select-searchable-label">{label}</label>}
      
      <div className="multi-select-searchable-container" onClick={handleInputClick}>
        {/* Search input with inline chips and client filter */}
        <div className="multi-select-searchable-content">
          {selectedOptions.length > 0 && (
            <div className="multi-select-searchable-chips">
              {selectedOptions.map(option => {
                const value = getOptionValue(option);
                const label = getOptionLabel(option);
                
                return (
                  <div key={value} className="multi-select-searchable-chip">
                    <span className="chip-label">{label}</span>
                    {!disabled && (
                      <button
                        type="button"
                        className="chip-remove"
                        onClick={(e) => handleRemoveOption(value, e)}
                      >
                        <X size={10} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          {showClientFilter && clients.length > 0 && (
            <select
              className="inline-client-filter"
              value={selectedClient}
              onChange={(e) => {
                e.stopPropagation();
                setSelectedClient(e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <option value="">All Clients</option>
              {clients.map((client) => {
                const clientName = typeof client === 'string' ? client : (client.name || client);
                const clientKey = typeof client === 'string' ? client : (client.id || client.name || client);
                return (
                  <option key={clientKey} value={clientName}>
                    {clientName}
                  </option>
                );
              })}
            </select>
          )}
          
          <input
            ref={inputRef}
            type="text"
            className="multi-select-searchable-input"
            placeholder={selectedOptions.length === 0 ? placeholder : ''}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setIsOpen(true);
              setHighlightedIndex(-1);
            }}
            onFocus={() => setIsOpen(true)}
            disabled={disabled}
          />
        </div>
        
        {selectedOptions.length > 0 && !disabled && (
          <button
            type="button"
            className="clear-all-btn"
            onClick={handleClearAll}
            title="Clear all"
          >
            ×
          </button>
        )}
      </div>
      
      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="multi-select-searchable-dropdown">
          <div className="multi-select-searchable-options" ref={optionsRef}>
            {loading ? (
              <div className="multi-select-searchable-loading">Loading contacts...</div>
            ) : filteredOptions.length === 0 ? (
              <div className="multi-select-searchable-no-options">
                {searchQuery ? `No contacts matching "${searchQuery}"` : noOptionsText}
              </div>
            ) : (
              <>
                {searchQuery && (
                  <div className="multi-select-searchable-results-count">
                    {filteredOptions.length} contact{filteredOptions.length !== 1 ? 's' : ''} found
                  </div>
                )}
                {filteredOptions.map((option, index) => {
                  const value = getOptionValue(option);
                  const label = getOptionLabel(option);
                  const email = getOptionEmail(option);
                  const isSelected = selected.includes(value);
                  
                  return (
                    <div
                      key={value}
                      className={`multi-select-searchable-option ${
                        isSelected ? 'selected' : ''
                      } ${index === highlightedIndex ? 'highlighted' : ''}`}
                      onClick={() => handleToggleOption(option)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      <div className="option-content">
                        <div className="option-label">{label}</div>
                        {email && <div className="option-email">{email}</div>}
                      </div>
                      {isSelected && (
                        <Check size={16} className="option-check" />
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectSearchable;